import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { NoteEditor } from '#/shared/ui/note-editor'

import {
  CAMPAIGN_PREVIEW_TEXT_LIMIT,
  CAMPAIGN_SEND_AS,
  CAMPAIGN_SUBJECT_LIMIT,
} from '../model/use-campaign-composer'
import type {
  CampaignSendAs,
  UseCampaignComposerResult,
} from '../model/use-campaign-composer'
import { CampaignComposerControls } from './campaign-composer-controls'

const SEND_AS_ORDER: readonly CampaignSendAs[] = [
  CAMPAIGN_SEND_AS.thread,
  CAMPAIGN_SEND_AS.newSubject,
]

interface CampaignEmailComposerProps {
  composer: UseCampaignComposerResult
  /** Step 1's subject — what a thread reply inherits. Absent before step 1 exists. */
  threadSubject?: string
}

function Chip({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'cursor-pointer rounded-md border px-2 py-1 text-xs font-medium transition-colors',
        selected
          ? 'border-campaign-running bg-avatar-blue-soft text-campaign-running'
          : 'border-gray-200 bg-white text-warm-500 hover:bg-gray-50',
      )}
    >
      {label}
    </button>
  )
}

/** A labelled single-line field with the inbox's character ceiling beside it. */
function CountedField({
  label,
  value,
  onChange,
  limit,
  emphasis,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  limit: number
  emphasis?: boolean
}) {
  return (
    <label
      className={cn(
        'flex items-center gap-2 rounded-lg border bg-white px-3 py-2.5',
        emphasis
          ? 'border-campaign-running ring-1 ring-campaign-running ring-inset'
          : 'border-gray-200',
      )}
    >
      <span className="shrink-0 text-xs text-warm-500">{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-w-0 flex-1 bg-transparent text-sm font-medium text-announce-ink outline-none"
      />
      <span className="shrink-0 text-xs text-warm-500 tabular-nums">
        {value.length} / {limit}
      </span>
    </label>
  )
}

function NewSubjectFields({
  composer,
}: {
  composer: UseCampaignComposerResult
}) {
  const { t } = useTranslation()
  return (
    <>
      <p className="text-xs text-warm-500">
        {t('campaigns.composer.newSubjectNote')}
      </p>
      <CountedField
        emphasis
        label={t('campaigns.composer.subject')}
        value={composer.subject}
        onChange={composer.setSubject}
        limit={CAMPAIGN_SUBJECT_LIMIT}
      />
      <CountedField
        label={t('campaigns.composer.previewText')}
        value={composer.previewText}
        onChange={composer.setPreviewText}
        limit={CAMPAIGN_PREVIEW_TEXT_LIMIT}
      />
      <p className="text-xs text-warm-500">
        {t('campaigns.composer.subjectHint')}
      </p>
      <div className="flex flex-col gap-2 rounded-[10px] bg-paper-avatar p-3">
        <p className="text-2xs font-medium text-warm-500">
          {t('campaigns.composer.inboxPreview')}
        </p>
        <div className="flex flex-col gap-0.5 rounded-lg bg-white px-3 py-2.5 text-sm leading-5">
          <p className="font-medium text-announce-ink">{composer.subject}</p>
          <p className="text-warm-500">{composer.previewText}</p>
        </div>
      </div>
    </>
  )
}

function LockedSubjectRow({ threadSubject }: { threadSubject?: string }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-paper-avatar px-3 py-2.5">
      <span className="text-xs text-warm-500">
        {t('campaigns.composer.subject')}
      </span>
      <span className="text-sm font-medium text-warm-500">
        {threadSubject != null
          ? t('campaigns.composer.threadSubject', { subject: threadSubject })
          : t('campaigns.composer.threadSubjectPending')}
      </span>
      <span className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-warm-500">
        {t('campaigns.composer.lockedByThread')}
      </span>
    </div>
  )
}

export function CampaignEmailComposer({
  composer,
  threadSubject,
}: CampaignEmailComposerProps) {
  const { t } = useTranslation()
  const isNewSubject = composer.sendAs === CAMPAIGN_SEND_AS.newSubject
  return (
    <div className="flex flex-col gap-2.5 p-3.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-warm-500">
          {t('campaigns.composer.sendAs')}
        </span>
        {SEND_AS_ORDER.map((option) => (
          <Chip
            key={option}
            label={t(`campaigns.composer.sendAsOption.${option}`)}
            selected={composer.sendAs === option}
            onClick={() => composer.setSendAs(option)}
          />
        ))}
      </div>

      {isNewSubject ? (
        <NewSubjectFields composer={composer} />
      ) : (
        <>
          <p className="text-xs text-warm-500">
            {t('campaigns.composer.threadNote')}
          </p>
          <LockedSubjectRow threadSubject={threadSubject} />
        </>
      )}

      {/* The shared rich-text editor, with only the marks this step is allowed
          to carry: no images and no table blocks, which is what the note under
          it promises and what keeps the mail out of Promotions. */}
      <NoteEditor
        enableLink
        value={composer.body}
        onChange={composer.setBody}
        placeholder={t('campaigns.composer.messageLabel')}
        minHeight={110}
        className="rounded-lg border border-gray-200"
      />
      <p className="text-xs text-warm-500">
        {t('campaigns.composer.formatNote')}
      </p>
      <p className="text-xs text-warm-500">
        {t('campaigns.composer.emailFooter')}
      </p>
      <CampaignComposerControls composer={composer} />
    </div>
  )
}
